import React, { useState } from 'react';
import { FormField } from '@/components/form-builder/FormBuilder';
import { Code2 } from 'lucide-react';
import CodeEditorModal from '@/components/ui/CodeEditorModal';
import { Building as CBuilding, FilePlus as CDocumentAdd, Smartphone as CMobile, AlertTriangle as CWarningAlt } from 'lucide-react';

interface FieldPropertiesPanelProps {
 field: FormField;
 allFields?: FormField[]; // All form fields for conditional logic dropdown
 onUpdate: (updates: Partial<FormField>) => void;
 onClose: () => void;
}

const FieldPropertiesPanel: React.FC<FieldPropertiesPanelProps> = ({
 field,
 allFields = [],
 onUpdate,
 onClose,
}) => {
 const [showConditional, setShowConditional] = useState(!!field.conditional);
 const [showValidation, setShowValidation] = useState(!!field.validation);
 const [showEvents, setShowEvents] = useState(!!field.events);
 const [eventEditorState, setEventEditorState] = useState<{
 isOpen: boolean;
 eventType: 'onChange' | 'onBlur' | 'onFocus' | 'onValidate' | null;
 code: string;
 }>({
 isOpen: false,
 eventType: null,
 code: '',
 });

 const handleOptionsChange = (index: number, value: string) => {
 const newOptions = [...(field.options || [])];
 newOptions[index] = value;
 onUpdate({ options: newOptions });
 };

 const handleAddOption = () => {
 const newOptions = [...(field.options || []), `Opção ${(field.options?.length || 0) + 1}`];
 onUpdate({ options: newOptions });
 };

 const handleRemoveOption = (index: number) => {
 const newOptions = [...(field.options || [])];
 newOptions.splice(index, 1);
 onUpdate({ options: newOptions });
 };

 const openEventEditor = (eventType: 'onChange' | 'onBlur' | 'onFocus' | 'onValidate', currentCode: string) => {
 setEventEditorState({
 isOpen: true,
 eventType,
 code: currentCode || '',
 });
 };

 const saveEventCode = (code: string) => {
 if (eventEditorState.eventType) {
 onUpdate({
 events: {
 ...(field.events || {}),
 [eventEditorState.eventType]: code,
 }
 });
 }
 setEventEditorState({
 isOpen: false,
 eventType: null,
 code: '',
 });
 };

 const getCodePreview = (code: string) => {
 if (!code) return '// Nenhum código definido';
 const lines = code.split('\n');
 const preview = lines.slice(0, 2).join('\n');
 const hasMore = lines.length > 2;
 return preview + (hasMore ? `\n...` : '');
 };

 return (
 <div className="flex flex-col h-full">
 {/* Header */}
 <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
 <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
 Propriedades do Campo
 </h3>
 <button
 onClick={onClose}
 className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
 >
 <i className="bi bi-x-lg"></i>
 </button>
 </div>

 {/* Content */}
 <div className="flex-1 overflow-y-auto p-4 space-y-6">
 {/* Basic Info */}
 <div className="space-y-4">
 <div>
 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
 Tipo de Campo
 </label>
 <div className="px-3 py-2 bg-gray-100 dark:bg-gray-900 rounded text-sm text-gray-600 dark:text-gray-400 capitalize">
 {field.fieldType}
 </div>
 </div>

 <div>
 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
 Label do Campo *
 </label>
 <input
 type="text"
 value={field.fieldLabel}
 onChange={(e) => onUpdate({ fieldLabel: e.target.value })}
 className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400 text-sm"
 placeholder={'Ex nome completo'}
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
 ID do Campo *
 </label>
 <input
 type="text"
 value={field.fieldId}
 onChange={(e) => onUpdate({ fieldId: e.target.value })}
 className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400 text-sm font-mono"
 placeholder={'Ex nome completo'}
 />
 <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
 Usado para identificar o campo nos dados
 </p>
 </div>

 <div>
 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
 Placeholder
 </label>
 <input
 type="text"
 value={field.fieldPlaceholder || ''}
 onChange={(e) => onUpdate({ fieldPlaceholder: e.target.value })}
 className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400 text-sm"
 placeholder={'Ex digite seu nome'}
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
 Valor Padrão
 </label>
 <input
 type="text"
 value={field.defaultValue || ''}
 onChange={(e) => onUpdate({ defaultValue: e.target.value })}
 className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400 text-sm"
 placeholder="Valor inicial"
 />
 </div>

 <div className="flex items-center justify-between">
 <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
 Campo Obrigatório
 </label>
 <button
 onClick={() => onUpdate({ required: !field.required })}
 className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
 field.required ? 'bg-brand-600' : 'bg-gray-200 dark:bg-gray-700'
 }`}
 >
 <span
 className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
 field.required ? 'translate-x-6' : 'translate-x-1'
 }`}
 />
 </button>
 </div>

 <div>
 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
 Step (Etapa)
 </label>
 <input
 type="number"
 min="1"
 value={field.step || 1}
 onChange={(e) => onUpdate({ step: parseInt(e.target.value) || 1 })}
 className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400 text-sm"
 placeholder="1"
 />
 <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
 Em qual etapa este campo deve aparecer (para formulários multi-step)
 </p>
 </div>
 </div>

 {/* Options for select, checkbox, radio */}
 {(field.fieldType === 'select' || field.fieldType === 'checkbox' || field.fieldType === 'radio') && (
 <div className="space-y-4 pt-6 border-t border-gray-200 dark:border-gray-700">
 <div className="flex items-center justify-between">
 <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
 Opções
 </label>
 <button
 onClick={handleAddOption}
 className="px-3 py-1 bg-brand-600 hover:bg-brand-700 text-white text-xs rounded-lg transition-colors"
 >
 <i className="bi bi-plus-lg mr-1"></i>
 Adicionar
 </button>
 </div>
 <div className="space-y-2">
 {field.options?.map((option, index) => (
 <div key={index} className="flex items-center space-x-2">
 <input
 type="text"
 value={option}
 onChange={(e) => handleOptionsChange(index, e.target.value)}
 className="flex-1 px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400 text-sm"
 placeholder={`Opção ${index + 1}`}
 />
 <button
 onClick={() => handleRemoveOption(index)}
 className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
 >
 <i className="bi bi-trash text-sm"></i>
 </button>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Validation Rules */}
 <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
 <button
 onClick={() => setShowValidation(!showValidation)}
 className="flex items-center justify-between w-full text-sm font-medium text-gray-700 dark:text-gray-300 mb-3"
 >
 <span>{'Regras de validacao'}</span>
 <i className={`bi bi-chevron-${showValidation ? 'up' : 'down'}`}></i>
 </button>
 {showValidation && (
 <div className="space-y-4">
 {field.fieldType === 'text' || field.fieldType === 'textarea' ? (
 <>
 <div>
 <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
 Comprimento Mínimo
 </label>
 <input
 type="number"
 value={field.validation?.min || ''}
 onChange={(e) => onUpdate({
 validation: { ...field.validation, min: parseInt(e.target.value) || undefined }
 })}
 className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-sm"
 placeholder="Min"
 />
 </div>
 <div>
 <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
 Comprimento Máximo
 </label>
 <input
 type="number"
 value={field.validation?.max || ''}
 onChange={(e) => onUpdate({
 validation: { ...field.validation, max: parseInt(e.target.value) || undefined }
 })}
 className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-sm"
 placeholder="Max"
 />
 </div>
 <div>
 <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
 Padrão (Regex)
 </label>
 <input
 type="text"
 value={field.validation?.pattern || ''}
 onChange={(e) => onUpdate({
 validation: { ...field.validation, pattern: e.target.value }
 })}
 className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-sm font-mono"
 placeholder="Ex: [A-Za-z]+"
 />
 <div className="mt-2 flex flex-wrap gap-2">
 <button
 type="button"
 onClick={() => onUpdate({ validation: { ...field.validation, pattern: '^[0-9]{11}$' } })}
 className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
 title={'Cpf11 digitos'}
 >
 <CDocumentAdd size={14} /> CPF
 </button>
 <button
 type="button"
 onClick={() => onUpdate({ validation: { ...field.validation, pattern: '^[0-9]{14}$' } })}
 className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
 title={'Cnpj14 digitos'}
 >
 <CBuilding size={14} /> CNPJ
 </button>
 <button
 type="button"
 onClick={() => onUpdate({ validation: { ...field.validation, pattern: '^\\([0-9]{2}\\) [0-9]{4,5}-[0-9]{4}$' } })}
 className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
 title="(11) 98765-4321"
 >
 <CMobile size={14} /> Tel
 </button>
 <button
 type="button"
 onClick={() => onUpdate({ validation: { ...field.validation, pattern: '^[0-9]{5}-[0-9]{3}$' } })}
 className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
 title="12345-678"
 >
 CEP
 </button>
 </div>
 </div>
 </>
 ) : null}
 {field.fieldType === 'number' && (
 <>
 <div>
 <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
 Valor Mínimo
 </label>
 <input
 type="number"
 value={field.validation?.min || ''}
 onChange={(e) => onUpdate({
 validation: { ...field.validation, min: parseFloat(e.target.value) || undefined }
 })}
 className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-sm"
 placeholder="Min"
 />
 </div>
 <div>
 <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
 Valor Máximo
 </label>
 <input
 type="number"
 value={field.validation?.max || ''}
 onChange={(e) => onUpdate({
 validation: { ...field.validation, max: parseFloat(e.target.value) || undefined }
 })}
 className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-sm"
 placeholder="Max"
 />
 </div>
 </>
 )}
 <div>
 <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
 Mensagem de Erro
 </label>
 <input
 type="text"
 value={field.validation?.message || ''}
 onChange={(e) => onUpdate({
 validation: { ...field.validation, message: e.target.value }
 })}
 className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-sm"
 placeholder="Mensagem customizada"
 />
 </div>
 </div>
 )}
 </div>

 {/* Conditional Logic */}
 <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
 <button
 onClick={() => setShowConditional(!showConditional)}
 className="flex items-center justify-between w-full text-sm font-medium text-gray-700 dark:text-gray-300 mb-3"
 >
 <span>{'Logica condicional'}</span>
 <i className={`bi bi-chevron-${showConditional ? 'up' : 'down'}`}></i>
 </button>
 {showConditional && (
 <div className="space-y-4">
 <p className="text-xs text-gray-500 dark:text-gray-400">
 Este campo será exibido apenas se a condição for verdadeira
 </p>
 <div>
 <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
 Depende do Campo
 </label>
 <select
 value={field.conditional?.field || ''}
 onChange={(e) => onUpdate({
 conditional: { ...field.conditional, field: e.target.value, operator: field.conditional?.operator || 'equals', value: field.conditional?.value || '' }
 })}
 className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-sm"
 >
 <option value="">Selecione um campo...</option>
 {allFields
 .filter(f => f.id !== field.id) // Don't show the current field
 .map(f => (
 <option key={f.id} value={f.fieldId}>
 {f.fieldLabel} ({f.fieldType})
 </option>
 ))}
 </select>
 {allFields.length === 0 && (
 <p className="text-xs text-brand-600 dark:text-brand-400 mt-1">
 <CWarningAlt size={14} /> Adicione outros campos ao formulário primeiro
 </p>
 )}
 </div>
 <div>
 <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
 Operador
 </label>
 <select
 value={field.conditional?.operator || 'equals'}
 onChange={(e) => onUpdate({
 conditional: { ...field.conditional!, operator: e.target.value as any }
 })}
 className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-sm"
 >
 <option value="equals">Igual a</option>
 <option value="not_equals">Diferente de</option>
 <option value="contains">{'Contem'}</option>
 <option value="greater_than">Maior que</option>
 <option value="less_than">Menor que</option>
 </select>
 </div>
 <div>
 <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
 Valor
 </label>
 <input
 type="text"
 value={field.conditional?.value || ''}
 onChange={(e) => onUpdate({
 conditional: { ...field.conditional!, value: e.target.value }
 })}
 className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-sm"
 placeholder={'Valor de comparacao'}
 />
 </div>
 {field.conditional?.field && (
 <button
 onClick={() => onUpdate({ conditional: undefined })}
 className="w-full px-3 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
 >
 Remover Condição
 </button>
 )}
 </div>
 )}
 </div>

 {/* JavaScript Events */}
 <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
 <button
 onClick={() => setShowEvents(!showEvents)}
 className="flex items-center justify-between w-full text-sm font-medium text-gray-700 dark:text-gray-300 mb-3"
 >
 <span>Eventos JavaScript</span>
 <i className={`bi bi-chevron-${showEvents ? 'up' : 'down'}`}></i>
 </button>
 {showEvents && (
 <div className="space-y-4">
 <p className="text-xs text-gray-500 dark:text-gray-400">
 Execute código JavaScript personalizado em eventos do campo
 </p>

 <div>
 <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
 onChange
 <span className="text-xs text-gray-500 ml-2">(quando o valor muda)</span>
 </label>
 <div className="relative group">
 <div className="bg-gray-900 rounded-lg border border-gray-700 p-3">
 <pre className="text-xs text-gray-300 font-mono whitespace-pre-wrap">
 {getCodePreview(field.events?.onChange || '')}
 </pre>
 </div>
 <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
 <button
 onClick={() => openEventEditor('onChange', field.events?.onChange || '')}
 className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white text-xs rounded-lg flex items-center gap-1.5"
 >
 <Code2 size={14} />
 Editar
 </button>
 </div>
 </div>
 </div>

 <div>
 <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
 onBlur
 <span className="text-xs text-gray-500 ml-2">(quando perde o foco)</span>
 </label>
 <div className="relative group">
 <div className="bg-gray-900 rounded-lg border border-gray-700 p-3">
 <pre className="text-xs text-gray-300 font-mono whitespace-pre-wrap">
 {getCodePreview(field.events?.onBlur || '')}
 </pre>
 </div>
 <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
 <button
 onClick={() => openEventEditor('onBlur', field.events?.onBlur || '')}
 className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white text-xs rounded-lg flex items-center gap-1.5"
 >
 <Code2 size={14} />
 Editar
 </button>
 </div>
 </div>
 </div>

 <div>
 <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
 onFocus
 <span className="text-xs text-gray-500 ml-2">(quando ganha o foco)</span>
 </label>
 <div className="relative group">
 <div className="bg-gray-900 rounded-lg border border-gray-700 p-3">
 <pre className="text-xs text-gray-300 font-mono whitespace-pre-wrap">
 {getCodePreview(field.events?.onFocus || '')}
 </pre>
 </div>
 <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
 <button
 onClick={() => openEventEditor('onFocus', field.events?.onFocus || '')}
 className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white text-xs rounded-lg flex items-center gap-1.5"
 >
 <Code2 size={14} />
 Editar
 </button>
 </div>
 </div>
 </div>

 <div>
 <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
 onValidate
 <span className="text-xs text-gray-500 ml-2">{'Validacao customizada'}</span>
 </label>
 <div className="relative group">
 <div className="bg-gray-900 rounded-lg border border-gray-700 p-3">
 <pre className="text-xs text-gray-300 font-mono whitespace-pre-wrap">
 {getCodePreview(field.events?.onValidate || '')}
 </pre>
 </div>
 <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
 <button
 onClick={() => openEventEditor('onValidate', field.events?.onValidate || '')}
 className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white text-xs rounded-lg flex items-center gap-1.5"
 >
 <Code2 size={14} />
 Editar
 </button>
 </div>
 </div>
 </div>

 <div className="bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-800 rounded-lg p-3">
 <p className="text-xs font-semibold text-brand-700 dark:text-brand-300 mb-2">
 Variáveis disponíveis:
 </p>
 <ul className="text-xs text-brand-600 dark:text-brand-400 space-y-1">
 <li><code className="bg-brand-100 dark:bg-brand-900/40 px-1 rounded">value</code> - valor atual do campo</li>
 <li><code className="bg-brand-100 dark:bg-brand-900/40 px-1 rounded">form</code> {'Todos os valores do formulario'}</li>
 <li><code className="bg-brand-100 dark:bg-brand-900/40 px-1 rounded">field</code> - elemento DOM do campo</li>
 </ul>
 <p className="text-xs text-brand-600 dark:text-brand-400 mt-2">
 {'Para validacao customizada retorne'}<code className="bg-brand-100 dark:bg-brand-900/40 px-1 rounded">true</code> se válido, ou uma string com a mensagem de erro.
 </p>
 </div>

 {(field.events?.onChange || field.events?.onBlur || field.events?.onFocus || field.events?.onValidate) && (
 <button
 onClick={() => onUpdate({ events: undefined })}
 className="w-full px-3 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
 >
 Remover Todos os Eventos
 </button>
 )}
 </div>
 )}
 </div>
 </div>

 {/* Event Code Editor Modal */}
 <CodeEditorModal
 isOpen={eventEditorState.isOpen}
 onClose={() => setEventEditorState({ isOpen: false, eventType: null, code: '' })}
 onSave={saveEventCode}
 initialCode={eventEditorState.code}
 title={`Editar ${eventEditorState.eventType || 'Evento'}`}
 language="javascript"
 />
 </div>
 );
};

export default FieldPropertiesPanel;
